// src/components/admin/AdminSettingsPanel.js
import React, { useState, useEffect } from 'react';

const AdminSettingsPanel = () => {
  // Initial state setup
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editedSettings, setEditedSettings] = useState({});
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Fetch settings from backend
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch('/api/admin/settings', {
          credentials: 'include',
        });
        
        if (response.status === 403) {
          throw new Error('Unauthorized: Admin access required');
        }
        
        if (!response.ok) {
          throw new Error('Failed to fetch settings');
        }
        
        const data = await response.json();
        setSettings(data);
        
        // Initialize editedSettings with current values
        const initialEdited = {};
        data.forEach(setting => {
          initialEdited[setting.setting_key] = setting.setting_value;
        });
        setEditedSettings(initialEdited);
      } catch (err) {
        console.error('Error fetching settings:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Handle input change
  const handleInputChange = (key, value) => {
    setEditedSettings({
      ...editedSettings,
      [key]: value
    });
  };

  // Handle saving a single setting
  const saveSetting = async (key) => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage('');
      
      const response = await fetch('/api/admin/settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          key,
          value: editedSettings[key]
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to save setting');
      }
      
      setSuccessMessage(`${key} updated successfully`);
      
      // Update the settings array with the new value
      setSettings(settings.map(setting => 
        setting.setting_key === key 
          ? { ...setting, setting_value: editedSettings[key] }
          : setting
      ));
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving setting:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Group settings by category
  const groupSettings = (settingsArray) => {
    const groups = {
      'Themes & Mechanics': [],
      'Player Experience': [],
      'Popularity': [],
      'Other': []
    };
    
    settingsArray.forEach(setting => {
      const key = setting.setting_key;
      
      if (key.includes('THEME') || key.includes('MECHANIC')) {
        groups['Themes & Mechanics'].push(setting);
      } else if (key.includes('PLAYER') || key.includes('PLAY_TIME') || key.includes('COMPLEXITY')) {
        groups['Player Experience'].push(setting);
      } else if (key.includes('POPULARITY') || key.includes('DOWNVOTE')) {
        groups['Popularity'].push(setting);
      } else {
        groups['Other'].push(setting);
      }
    });
    
    return groups;
  };

  // Format setting key for display
  const formatSettingKey = (key) => {
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
  // Handle reset to default
  const resetToDefault = async (key, defaultValue) => {
    try {
      setEditedSettings({
        ...editedSettings,
        [key]: defaultValue
      });
      await saveSetting(key);
      setSuccessMessage(`${key} reset to default value`);
    } catch (err) {
      console.error('Error resetting setting:', err);
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-6">
        <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
        <p className="text-gray-400 mt-2">Loading settings...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 text-red-200">
        <p>Error: {error}</p>
      </div>
    );
  }

  const groupedSettings = groupSettings(settings);

  return (
    <div>
      
      {successMessage && (
        <div className="mb-4 bg-green-900/30 border border-green-700 p-3 rounded text-green-200">
          {successMessage}
        </div>
      )}
      
      {Object.entries(groupedSettings).map(([groupName, groupSettings]) => (
        groupSettings.length > 0 && (
          <div key={groupName} className="mb-8">
            <h3 className="text-lg font-medium mb-4 text-blue-300 border-b border-gray-700 pb-2">{groupName}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {groupSettings.map(setting => (
                <div key={setting.setting_key} className="bg-gray-750 p-4 rounded-lg border border-gray-700">
                  {/* Setting title and description */}
                  <h3 className="text-lg font-medium text-blue-300 mb-2">
                    {setting.description ? setting.description.split(':')[0] : formatSettingKey(setting.setting_key)}
                  </h3>
                  {setting.description && (
                    <p className="text-sm text-gray-300 mb-3">
                      {setting.description.includes(':') ? setting.description.split(':')[1].trim() : setting.description}
                    </p>
                  )}
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="0.1"
                      value={editedSettings[setting.setting_key]}
                      onChange={(e) => handleInputChange(setting.setting_key, e.target.value)}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      aria-label={formatSettingKey(setting.setting_key)}
                    />
                    <button
                      onClick={() => saveSetting(setting.setting_key)}
                      disabled={saving}
                      className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        const defaultSetting = {
                          'THEME_WEIGHT': 3,
                          'MECHANIC_WEIGHT': 4,
                          'NON_MATCHING_THEME_PENALTY': 0.5,
                          'NON_MATCHING_MECHANIC_PENALTY': 0.5,
                          'PLAYER_COUNT_WEIGHT': 2,
                          'PLAY_TIME_WEIGHT': 1,
                          'COMPLEXITY_WEIGHT': 2,
                          'POPULARITY_BONUS_WEIGHT': 2,
                          'DOWNVOTE_PENALTY_FACTOR': 0.5,
                          'PLAY_TIME_SIMILARITY_THRESHOLD': 30
                        }[setting.setting_key];
                        
                        if (defaultSetting !== undefined) {
                          resetToDefault(setting.setting_key, defaultSetting);
                        }
                      }}
                      disabled={saving}
                      className="px-2 py-2 bg-gray-600 hover:bg-gray-700 text-gray-300 rounded transition-colors text-xs"
                      title="Reset to default value"
                    >
                      Reset
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      ))}
    </div>
  );
};

export default AdminSettingsPanel;